// tcms_base_modalWindow.js
//
// @copyright    Copyright (c) 2020 CHECK24 Vergleichsportal Reise GmbH
// @author       Mark Kotsarev <mark.kotsarev@check24.de>
// @modified     3.12.2020
// ------------------------------------------------------------------------------

import('../../styles/ck24_modalWindow.scss');
import { TcmsBaseClass } from './TcmsBase.class.ts';

export class ModalWindow extends TcmsBaseClass {

    static modalElement = null; // only allow one modal element

    #content;

    constructor(content, options) {
        super();
        this.#content = content ?? '';

        const defaultConfigs = {
            triggerElem: null, // elem which opens the modal
            disableBackgroundScroll: false,
            mobileSheet: false,

            // Css Classes
            mobileSheetClass: 'modal-mobile-sheet',
            mainElemClasses: [],
            contentElemClasses: [],
            closeElemClasses: [
                'tcms-font-icon', 'icon-close-slim', 'ck24_modal-close'
            ]
        };

        this.options = this.mergeObject(defaultConfigs, options);
        this.create();
    }

    get content() {
        return this.#content;
    }

    set content(newContent) {
        this.changeContent(newContent);
    }

    /**
     * The getModalTemplate method returns a string containing the HTML for the modal.
     *
     * @return A string of html
     */
    static getModalTemplate() {
        return `
        <div class="ck24_modal">
            <div class="ck24_modal-box">
                <div class="ck24_modal-close" data-testid="tcms-widget-tam-sort-popup-close"></div>
                <div class="ck24_modal-content" data-testid="tcms-widget-tam-sort-popup"></div>
            </div>
        </div>
        `;
    }

    /**
     * Enable or disable background scroll
     */
    static setBackgroundLock(lock = true) {
        window.bodyScrollLock.bodyLock(lock);
    }

    /**
     * The create method creates the modal element and appends it to the document.
     * It also adds click event listeners to the close button and overlay, which calls this.close().
     *
     * @return The modal element
     */
    create() {
        // check if a modal element already exists
        if (!ModalWindow.modalElement) {
            // add modal template and append to body
            const template = ModalWindow.getModalTemplate();
            const parser = new DOMParser();

            ModalWindow.modalElement = parser.parseFromString(template, 'text/html').body.firstElementChild;
            document.body.insertAdjacentElement('beforeend', ModalWindow.modalElement);

            // add close events
            ModalWindow.modalElement.addEventListener('click', (e) => {
                if (e.target !== e.currentTarget) return;
                this.close();
            });
            ModalWindow.modalElement.querySelector('.ck24_modal-close').addEventListener('click', this.close);
        }

        // add click event on trigger element
        if (this.options.triggerElem) {
            this.options.triggerElem.addEventListener('click', () => {
                this.updateModal();
                this.open();
            });
        }

        // update modal and ensure it has values from the latest instance if modal state is not open
        ModalWindow.modalElement.classList.contains('ck24_modal-show') || this.updateModal();

        return ModalWindow.modalElement;
    }

    /**
     * The open method opens the modal window.
     */
    open() {
        if (this.options.disableBackgroundScroll) {
            ModalWindow.setBackgroundLock();
        }
        this.updateModal();
        ModalWindow.modalElement.classList.add('ck24_modal-show');
    }

    /**
     * The close method removes the modal-show class from the modalElement,
     * which hides it.
     */
    close() {
        ModalWindow.modalElement.classList.remove('ck24_modal-show');
        ModalWindow.setBackgroundLock(false);
    }

    /**
     * The updateModal method updates the content and css classes.
     * this ensures that the modal has its values from the latest instance
     */
    updateModal() {
        ModalWindow.modalElement.className = 'ck24_modal'; // reset css classes
        ModalWindow.modalElement.classList.add(...this.options.mainElemClasses);
        ModalWindow.modalElement.querySelector('.ck24_modal-content').classList.add(...this.options.contentElemClasses);
        ModalWindow.modalElement.querySelector('.ck24_modal-close').classList.add(...this.options.closeElemClasses);

        if (this.options.mobileSheet && window.TCMS.isMobile) {
            ModalWindow.modalElement.classList.add(this.options.mobileSheetClass);
        }
        this.changeContent(this.#content);
    }

    /**
     * The changeContent method changes the content of the modal window.
     *
     * @param newContent Pass the new content to be displayed in the modal window
     */
    changeContent(newContent) {
        this.#content = newContent;

        if (ModalWindow.modalElement) {
            const modalContentInner = ModalWindow.modalElement.querySelector('.ck24_modal-content');

            modalContentInner.innerHTML = ''; // Clear previous content

            if (newContent instanceof Node) {
                modalContentInner.appendChild(newContent);
            } else {
                modalContentInner.innerHTML = newContent;
            }
        }
    }

}

export default ModalWindow;
